/* eslint-disable max-lines */
'use client'

import { type UIMessage, useChat } from '@ai-sdk/react'
import { ErrandResponse } from '@backend/schemas/src/errands'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { type ChatRequestOptions, DefaultChatTransport } from 'ai'
import { cva } from 'class-variance-authority'
import {
  ArrowDown,
  InfoIcon,
  Maximize2Icon,
  MessageSquarePlusIcon,
  Minimize2Icon,
  NotebookPenIcon,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import {
  createContext,
  forwardRef,
  type HTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useThreadsServiceGetV1IndividualsByIndividualIdThreadsKey } from '@/api/client/queries/common'
import { OpenAPI } from '@/api/client/requests'
import { useErrandsServiceGetV1IndividualsByIndividualIdErrandsByErrandIdActionsKey } from '@/api/client/queries/common'
import { useErrandReactionRealtime } from '@/api/hooks/useErrandReactionRealtime'
import { useErrands } from '@/api/hooks/useErrands'
import { useThread } from '@/api/hooks/useThread'
import { usePreviewDocumentFiles } from '@/hooks/useDocumentPreview'
import { DropzoneOverlay } from '@/components/molecules/dropzone-overlay'
import ChatInputArea from '@/components/organisms/ai-chat/chat-input-area'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { appConfig } from '@/config/app.config'
import { useFirebaseToken } from '@/hooks/useFirebaseUser/useFirebaseToken'
import { useSubscription } from '@/contexts/subscription-context'
import { useIndividuals } from '@/hooks/useIndividuals'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { AssistantActions } from '@/redux/reducers/assistant'
import authenticationService from '@/services/authentication/authentication'
import { getErrandChosenServiceTitles } from '@/utils/errand-services.utils'

import {
  CreateThreadSummaryNoteDialog,
  type CreateThreadSummaryNoteDialogHandle,
} from '../create-thread-summary-note-dialog'
import {
  ChatMessageAi,
  ChatMessageError,
  ChatMessageLoading,
  ChatMessageUser,
} from './chat-message'
import { ChatMessageAction } from './chat-message-action'
import { PromptSuggestions } from './prompt-suggestions'

type AiChatLayout = 'fullscreen' | 'sidebar'

const AiChatContext = createContext<{ layout: AiChatLayout }>({
  layout: 'fullscreen',
})

const useAiChatContext = () => {
  const context = useContext(AiChatContext)

  if (!context) {
    throw new Error('useAiChatContext must be used within a AiChatProvider')
  }

  return context
}

const inputAreaVariants = cva('relative', {
  defaultVariants: {
    layout: 'fullscreen',
  },
  variants: {
    layout: {
      fullscreen: 'px-6 md:px-9 md:pb-6',
      sidebar: 'px-4 pb-4',
    },
  },
})

type Props = {
  chatRequestOptions?: ChatRequestOptions
  layout?: AiChatLayout
  useChatOptions?: Parameters<typeof useChat>[0]
}

const AiChat = ({ chatRequestOptions, layout = 'fullscreen', useChatOptions }: Props) => {
  const token = useFirebaseToken()
  const { selectedIndividualId } = useIndividuals()
  const { canUseChat, isTrial } = useSubscription()
  const selectedIndividualIdRef = useRef(selectedIndividualId)
  selectedIndividualIdRef.current = selectedIndividualId
  const dispatch = useAppDispatch()
  const { i18n, t } = useTranslation()
  const activeThreadId = useAppSelector(state => state.assistant.activeThreadId)
  const isChatFullscreen = useAppSelector(state => state.assistant.isChatFullscreen)
  const pendingMessage = useAppSelector(state => state.assistant.pendingMessage)
  const activeMessageId = useAppSelector(state => state.assistant.activeMessageId)
  const pendingErrandReactionId = useAppSelector(state => state.assistant.pendingErrandReactionId)
  const navigate = useNavigate()
  const selectedErrandId = useAppSelector(state => state.settings.selectedErrandId)
  const { pathname } = useLocation()
  const { data: thread } = useThread(activeThreadId, selectedIndividualId)

  // Documents of the errand this chat belongs to, so a reaction's pills can open the real
  // preview. Mounted by the app shell rather than the errand page, the chat has no props to
  // receive these through — it resolves them from the errand it is already scoped to. Pills for
  // documents that are no longer on the errand fall back to the name stored on the message.
  const { data: errands = [] } = useErrands(selectedIndividualId)
  const previewDocumentFiles = usePreviewDocumentFiles()
  const selectedErrand = useMemo(
    () => errands.find(errand => errand.id === selectedErrandId),
    [errands, selectedErrandId],
  )
  const errandDocuments = useMemo(() => selectedErrand?.documents ?? [], [selectedErrand])
  // e.g. "Sjukhusundervisning, Omvårdnadsbidrag och Merkostnadsersättning" — for the chat intro.
  const errandServiceList = useMemo(() => {
    if (!selectedErrand) return ''
    return new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
      getErrandChosenServiceTitles(selectedErrand),
    )
  }, [selectedErrand, i18n.language])

  const previewableDocumentIds = useMemo(
    () => new Set(errandDocuments.map(document => document.id)),
    [errandDocuments],
  )

  // Notes still attached to the errand — the ones a pill can actually navigate to. A pill for a
  // note since deleted keeps its stored title but stops being clickable.
  const openableNoteIds = useMemo(
    () => new Set((selectedErrand?.notes ?? []).map(note => note.id)),
    [selectedErrand],
  )

  // Into the errand-scoped note route, not /notes/$noteId — the chat is only ever showing notes
  // in an errand's context, and leaving Ärenden to read one is the wrong place to land. Same
  // reasoning as the errand page's own "Lägg till anteckning" button.
  const onOpenNote = useCallback(
    (noteId: string) => {
      if (!selectedErrandId) return

      void navigate({
        params: { id: selectedErrandId, noteId },
        to: '/errands/$id/notes/$noteId',
      })
    },
    [navigate, selectedErrandId],
  )

  const onPreviewDocument = useCallback(
    (documentId: string) => {
      const document = errandDocuments.find(candidate => candidate.id === documentId)
      if (!document) return

      previewDocumentFiles(document, document.combinedFilePath)
    },
    [errandDocuments, previewDocumentFiles],
  )
  const queryClient = useQueryClient()
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: OpenAPI.BASE + `/v1/individuals/${selectedIndividualId}/threads`,
        headers: {
          ...OpenAPI.HEADERS,
        },
        async prepareSendMessagesRequest({ body, messages }) {
          const token = await authenticationService.getAccessToken()

          return {
            body: { ...body, message: messages[messages.length - 1] },
            headers: {
              Authorization: token ? `Bearer ${token}` : '',
            },
          }
        },
      }),
    [selectedIndividualId],
  )

  const { clearError, error, messages, sendMessage, setMessages, status } = useChat({
    ...useChatOptions,
    onData: message => {
      // The server emits this once it has persisted AI-generated errand actions, so refetch them
      // and the errand page updates live (race-free — the write is already done server-side).
      if (message.type === 'data-errand-actions') {
        queryClient.invalidateQueries({
          queryKey: [useErrandsServiceGetV1IndividualsByIndividualIdErrandsByErrandIdActionsKey],
        })
        return
      }
      if (message.type !== 'data-thread') return
      const threadId = (message.data as { threadId?: string }).threadId
      if (!threadId) return
      dispatch(AssistantActions.setActiveThreadId(threadId))
    },
    onFinish: () => {
      // Must be the generated query key — plain 'threads' matches nothing, so the
      // conversation list never refreshed after a message.
      queryClient.invalidateQueries({
        queryKey: [useThreadsServiceGetV1IndividualsByIndividualIdThreadsKey],
      })
    },
    transport: useMemo(
      () =>
        new DefaultChatTransport({
          headers: {
            ...OpenAPI.HEADERS,
          },
          async prepareSendMessagesRequest({ body, messages }) {
            const token = await authenticationService.getAccessToken()

            return {
              api: `${OpenAPI.BASE}/v1/individuals/${selectedIndividualIdRef.current}/threads`,
              body: { ...body, message: messages[messages.length - 1] },
              headers: {
                Authorization: token ? `Bearer ${token}` : '',
              },
            }
          },
        }),
      [],
    ),
  })

  // Proactive errand messages are written straight into the thread by background jobs, so the
  // open chat needs a nudge to pick them up.
  useErrandReactionRealtime({
    canRefresh: status === 'ready' || status === 'error',
    errandId: selectedErrandId,
    individualId: selectedIndividualId,
  })

  useEffect(() => {
    if (!thread) return

    setMessages(thread.messages as UIMessage[])
  }, [thread])

  const scrollRef = useRef<HTMLDivElement>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const dialogRef = useRef<CreateThreadSummaryNoteDialogHandle>(null)
  const [fileAttachments, setFileAttachments] = useState<File[]>([])
  const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
    accept: appConfig.aiChat.attachmentAllowedTypes,
    disabled: !appConfig.aiChat.allowFileUpload,
    maxSize: appConfig.aiChat.attachmentMaxSize,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDropAccepted(files) {
      setFileAttachments(f => [...f, ...files])
    },
    onDropRejected: fileRejections => {
      toast.error(
        t('aiChat.filesRejected', {
          maxSizeMb: appConfig.aiChat.attachmentMaxSize / (1024 * 1024),
        }),
      )
      console.log('fileRejections', fileRejections)
    },
  })

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return
      const { clientHeight, scrollHeight, scrollTop } = scrollRef.current
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight
      setShowScrollButton(distanceFromBottom > 40)
    }

    const scrollElement = scrollRef.current

    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll)
      handleScroll()
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  useEffect(() => {
    // An error belongs to the conversation that produced it — clear it whenever the
    // active thread changes, or it follows the user into the next conversation.
    clearError()

    if (!activeThreadId) {
      setMessages([])
    }
  }, [activeThreadId])

  const resetChat = () => {
    // Clear here rather than relying on the activeThreadId effect above: when a
    // request fails before the server assigns a thread id, activeThreadId is already
    // undefined, so dispatching undefined produces no state change, no re-render, and
    // that effect never runs — leaving the failed message and the error on screen with
    // an apparently dead button. clearError() is needed regardless; the effect only
    // ever cleared messages.
    clearError()
    setMessages([])
    dispatch(AssistantActions.setActiveThreadId(undefined))
  }

  // useEffect(() => {
  //   if (threadId && initialMessages.length > 0) {
  //     if (initialThreadId.current === threadId) return
  //     setMessages(initialMessages)
  //     initialThreadId.current = threadId
  //   }
  // }, [initialMessages, threadId])

  useEffect(() => {
    scrollToBottom()
  }, [messages.length])

  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({
      behavior: 'smooth',
      top: scrollRef.current.scrollHeight,
    })
  }, [])

  // Scroll to a specific message when requested (e.g. from an errand action's "Visa i chatten").
  // Runs after messages render so the target element exists; clears the request once handled.
  useEffect(() => {
    if (!activeMessageId) return
    const el = scrollRef.current?.querySelector(`[data-message-id="${activeMessageId}"]`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    dispatch(AssistantActions.setActiveMessageId(undefined))
  }, [activeMessageId, messages, dispatch])

  const onSubmitPrompt = useCallback(
    (text: string) => {
      // Guarded here rather than only on the textarea because this is what every other door
      // calls too: the prompt-suggestion chips, and the message the home quick-chat queues
      // through redux. The backend rejects with 402 either way; this keeps a trial from firing
      // a request it cannot use.
      if (!canUseChat) return

      sendMessage(
        { files: toFileList(fileAttachments), text },
        {
          ...chatRequestOptions,
          body: {
            ...chatRequestOptions?.body,
            errandId: selectedErrandId,
            individualId: selectedIndividualId,
            threadId: activeThreadId,
            currentPage: pathname,
          },
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        },
      )
      setFileAttachments([])
    },
    [
      fileAttachments,
      sendMessage,
      activeThreadId,
      selectedIndividualId,
      selectedErrandId,
      pathname,
      token,
      canUseChat,
    ],
  )

  useEffect(() => {
    if (!pendingMessage || !token) return
    dispatch(AssistantActions.setPendingMessage(undefined))
    onSubmitPrompt(pendingMessage)
  }, [pendingMessage, token, onSubmitPrompt, dispatch])

  const renderedMessages = useMemo(() => {
    return messages.map((message, index) => {
      if (message.role === 'user') {
        const attachments = message.parts.filter(part => part.type === 'file')
        const content = message.parts
          .filter(part => part.type === 'text')
          .map(({ text }) => text)
          .join('\n\n')

        return (
          <div key={message.id} data-message-id={message.id} className="scroll-mt-4">
            <ChatMessageUser attachments={attachments} key={message.id}>
              {content}
            </ChatMessageUser>
          </div>
        )
      }

      // Set by the backend on messages the assistant wrote on its own initiative; absent on
      // everything streamed in reply to the user.
      const reaction = message as {
        automated?: boolean
        sourceDocuments?: { id: string; name: string }[]
        sourceNotes?: { id: string; title: string }[]
        statusFrom?: ErrandResponse['status']
        statusTo?: ErrandResponse['status']
      }
      const isAutomated = reaction.automated === true
      // Describes what actually happened — never a generic "Funkity did something" label. A
      // reaction can cover an upload and a status change at once, so both parts can show up
      // together.
      const automatedLabel = isAutomated
        ? [
            (reaction.sourceDocuments?.length ?? 0) > 0
              ? t('aiChat.reaction.documentsUploaded', { count: reaction.sourceDocuments!.length })
              : null,
            (reaction.sourceNotes?.length ?? 0) > 0
              ? t('aiChat.reaction.notesAdded', { count: reaction.sourceNotes!.length })
              : null,
            reaction.statusFrom && reaction.statusTo
              ? t('errandsPage.timeline.statusChanged', {
                  from: t(`errandStatus.${reaction.statusFrom}`),
                  to: t(`errandStatus.${reaction.statusTo}`),
                })
              : null,
          ]
            .filter(Boolean)
            .join(' · ') || undefined
        : undefined

      return (
        <div key={message.id} data-message-id={message.id} className="scroll-mt-4">
          {message.parts.map((part, partIndex) => {
            if (part.type === 'step-start') return null
            const key = `${message.id}-${index}-${partIndex}`

            if (part.type === 'text') {
              return (
                <ChatMessageAi
                  automatedLabel={automatedLabel}
                  key={key}
                  onOpenNote={onOpenNote}
                  onPreviewDocument={onPreviewDocument}
                  openableNoteIds={openableNoteIds}
                  previewableDocumentIds={previewableDocumentIds}
                  sourceDocuments={reaction.sourceDocuments}
                  sourceNotes={reaction.sourceNotes}
                >
                  {part.text}
                </ChatMessageAi>
              )
            }

            // AI-generated errand actions (createDraft) render as cards.
            if (part.type === 'tool-createDraft') {
              return <ChatMessageAction key={key} part={part} />
            }

            // return <ChatMessageAi key={key}>{`${part.type} is not supported yet.`}</ChatMessageAi>
          })}
        </div>
      )
    })
  }, [messages, onOpenNote, onPreviewDocument, openableNoteIds, previewableDocumentIds])

  const isChatMessageLoading = useMemo(() => {
    if (status === 'submitted') return true

    if (status === 'streaming') {
      const lastMessage = messages.at(-1)
      if (!lastMessage) return false

      return (
        lastMessage.role === 'assistant' &&
        lastMessage.parts.findIndex(part => part.type === 'text' && part.text.length > 0) === -1
      )
    }
  }, [messages, status])

  const threadHasNote = !!thread?.note
  const isChatEmpty = messages.length === 0
  const isShowingThreadSummaryDialog =
    activeThreadId && (messages.length > 4 || (messages.length === 4 && status !== 'streaming'))

  // Status change / upload / note-add sets this (see the respective pages), and
  // `useErrandReactionRealtime` clears it once the automated reaction actually lands — see that
  // hook's doc comment. The timeout is a safety net for a pipeline that errors out silently
  // (e.g. a Cloud Task retrying) and never stamps `reactionAt`, so the bubble doesn't stay stuck.
  const isPendingErrandReaction = !!selectedErrandId && pendingErrandReactionId === selectedErrandId
  useEffect(() => {
    if (!isPendingErrandReaction) return

    const timeout = setTimeout(() => {
      dispatch(AssistantActions.setPendingErrandReaction(undefined))
    }, 120_000)

    return () => clearTimeout(timeout)
  }, [isPendingErrandReaction, dispatch])

  // The other scroll-to-bottom effect keys on `messages.length`, which doesn't change here — this
  // bubble isn't a message, just a stand-in shown while one is on its way. Without this, it renders
  // below the fold, hidden behind the sticky input area, until the user scrolls down themselves.
  useEffect(() => {
    if (!isPendingErrandReaction) return

    scrollToBottom()
  }, [isPendingErrandReaction, scrollToBottom])

  return (
    <AiChatContext.Provider value={{ layout }}>
      <AiChatContainer {...getRootProps()}>
        <input className="hidden pointer-events-none" {...getInputProps()} />
        <AiChatMessageListContainer ref={scrollRef}>
          <div className="sticky top-0 flex z-10 justify-end mb-4 gap-3">
            {!selectedErrandId && (
              <>
                <CreateThreadSummaryNoteDialog
                  ref={dialogRef}
                  // Summarising is another AI generation behind the chat's entitlement, so
                  // the trigger goes away with it rather than 402-ing on click.
                  showTrigger={!!isShowingThreadSummaryDialog && canUseChat}
                  threadId={activeThreadId}
                />
                {threadHasNote && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => dialogRef.current?.open()}
                        size="icon"
                        variant="outline"
                      >
                        <NotebookPenIcon />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">{t('aiChat.options.editNote')}</TooltipContent>
                  </Tooltip>
                )}
                {!isChatEmpty && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button onClick={resetChat} size="icon" variant="outline">
                        <MessageSquarePlusIcon />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">{t('aiChat.options.newChat')}</TooltipContent>
                  </Tooltip>
                )}
              </>
            )}
            {layout === 'sidebar' && (
              <div className="hidden md:contents">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() =>
                        dispatch(AssistantActions.setChatFullscreen(!isChatFullscreen))
                      }
                      size="icon"
                      variant="outline"
                    >
                      {isChatFullscreen ? <Minimize2Icon /> : <Maximize2Icon />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {isChatFullscreen
                      ? t('aiChat.options.exitFullscreen')
                      : t('aiChat.options.fullscreen')}
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
          <div className="max-w-2xl mx-auto w-full flex flex-col gap-4">
            {isChatEmpty && (
              <Alert variant="info">
                <InfoIcon />
                <AlertDescription>{t('aiChat.headsUpInfo')}</AlertDescription>
              </Alert>
            )}
            {isChatEmpty && selectedErrand && (
              <ChatMessageAi>
                {t('aiChat.errandIntro', { services: errandServiceList })}
              </ChatMessageAi>
            )}
            {renderedMessages}
            {(isChatMessageLoading || isPendingErrandReaction) && <ChatMessageLoading />}
            {error && <ChatMessageError error={error} />}
          </div>
        </AiChatMessageListContainer>
        <div className={inputAreaVariants({ layout })}>
          <div className="max-w-2xl mx-auto w-full">
            <AnimatePresence>
              {showScrollButton && (
                <motion.div
                  animate={{ opacity: 1 }}
                  className="absolute -top-4 left-1/2 -translate-y-full -translate-x-1/2 z-20"
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Button
                    className="rounded-full shadow-sm size-8"
                    onClick={scrollToBottom}
                    size="icon"
                    variant="secondary"
                  >
                    <ArrowDown className="text-text-secondary" size={18} />
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
            {isChatEmpty && canUseChat && (
              <div className="mb-3 px-1">
                <PromptSuggestions
                  onClickSuggestion={onSubmitPrompt}
                  onClickStaticSuggestion={onSubmitPrompt}
                />
              </div>
            )}
            <ChatInputArea
              // Locks composing but not reading: the conversation history is the user's own
              // content and stays available. Applies to a trial as well as to read-only —
              // chat is not part of the trial — with wording that says which.
              disabledMessage={t(
                isTrial ? 'subscription.chatTrialTooltip' : 'subscription.readOnlyTooltip',
              )}
              isDisabled={!canUseChat}
              isSubmitEnabled={status === 'ready'}
              onSubmit={onSubmitPrompt}
              {...(appConfig.aiChat.allowFileUpload && {
                fileAttachments: fileAttachments,
                onChangeFiles: files => setFileAttachments(files),
                onRequestFileAttachment: open,
              })}
            />
          </div>
        </div>
        {appConfig.aiChat.allowFileUpload && (
          <DropzoneOverlay isDragActive={isDragActive} message={t('aiChat.dropFilesOverlay')} />
        )}
      </AiChatContainer>
    </AiChatContext.Provider>
  )
}

const containerVariants = cva('bg-background', {
  defaultVariants: {
    layout: 'fullscreen',
  },
  variants: {
    layout: {
      fullscreen: 'p-0 [&>div]:h-[calc(100vh-96px)] [&>div]:md:h-[calc(100vh-64px)]',
      // h-full rather than a viewport calc: the parent chain (SidebarProvider down to the
      // ResizablePanel) already resolves to the real available height, svh-based so mobile
      // browser chrome doesn't cause overflow — a 100vh calc here would fight that and get
      // the input area clipped by an ancestor's overflow-hidden.
      sidebar:
        'h-full p-4 pl-0 [&>div]:h-full [&>div]:rounded-lg [&>div]:border [&>div]:border-subtle-01',
    },
  },
})

const AiChatContainer = ({ ...props }: HTMLAttributes<HTMLDivElement>) => {
  const { layout } = useAiChatContext()

  return (
    <div className={containerVariants({ layout })}>
      <div
        {...props}
        className={cn(
          'relative flex flex-col bg-layer-1',
          'w-full md:w-auto',
          'transition-all duration-300',
          props.className,
        )}
      >
        {props.children}
      </div>
    </div>
  )
}

const messageListVariants = cva(
  'w-full relative flex-1 gap-4 overflow-y-auto [mask-image:linear-gradient(to_top,transparent_0%,black_20px)] [scrollbar-color:hsl(var(--layer-3))_hsl(var(--layer-2))] [scrollbar-gutter:stable_both-edges] [scrollbar-width:thin]',
  {
    defaultVariants: {
      layout: 'fullscreen',
    },
    variants: {
      layout: {
        fullscreen: 'px-4 md:px-7 md:pb-6 py-8',
        // Matches the input area's own px-4 — was px-3, a hair tighter than the input
        // field beside it.
        sidebar: 'px-4 py-4',
      },
    },
  },
)

const AiChatMessageListContainer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ ...props }, ref) => {
    const { layout } = useAiChatContext()

    return (
      <div ref={ref} {...props} className={cn(messageListVariants({ layout }), props.className)} />
    )
  },
)

function toFileList(files: File[]): FileList {
  const dataTransfer = new DataTransfer()
  files.forEach(file => dataTransfer.items.add(file))

  return dataTransfer.files
}

export default AiChat
